import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { CopyIcon, PlusIcon, TrashIcon } from 'lucide-react';

import { openModal } from '@/components/modals/modals.utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { projectAtom } from '@/features/navigation/atoms/projectAtom';
import { getNumberFormatter } from '@/utils/getNumberFormatter';
import { parseSearchParamsToSearchQuery } from '@/utils/parseSearchParamsToSearchQuery';

import { useDeleteEstate } from '../estates/api/estates';
import { ProjectEstatesInfiniteScroll } from '../estates/components/project-estates-infinite-scroll';
import { Toolbar } from '../estates/components/toolbar';
import { getEstate } from '../estates-property/api/estate';
import { EstateImage } from '../estates-property/components/estate-image';
import { getFloorLabel } from '../estates-property/utils/floorLabelHandler';
import {
  getProjectEstatesOptions,
  type ProjectEstates,
  useProjectEstates,
} from '../projects/api/project-estates';
import type { Project } from '../projects/api/projects';

const url = '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout/objects';
const route = getRouteApi(url);

type ObjectsTableProps = {
  project: Project;
};

export const ObjectsTable: React.FC<ObjectsTableProps> = ({ project }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = route.useNavigate();
  const { filters } = route.useSearch();
  const parsedFilters = parseSearchParamsToSearchQuery(filters);

  const setProject = useSetAtom(projectAtom);

  const queryProjectEstatesOptions = useMemo(
    () => ({
      projectId: project?.id,
      query: {
        ...(Object.keys(parsedFilters).length ? { filters: parsedFilters } : {}),
      },
    }),
    [project, parsedFilters]
  );

  const { data } = useProjectEstates(queryProjectEstatesOptions);

  const { mutate: deleteEstate } = useDeleteEstate({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('deleteEstateSuccess'));
        queryClient.invalidateQueries({
          queryKey: getProjectEstatesOptions(queryProjectEstatesOptions).queryKey,
        });
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const { estates, totalEstates } = useMemo(
    () => ({
      estates: data.estates,
      totalEstates: data.count,
    }),
    [data]
  );

  const navigateToEstatePage = useCallback(
    (id: string) => {
      if (project?.id && project?.name) {
        setProject({ id: project.id, name: project.name });
      } else {
        setProject(null);
      }
      navigate({ to: '/estates/$estateId', params: { estateId: id } });
    },
    [navigate, project?.id, project?.name, setProject]
  );

  const handleCopyClick = async (estateId: string) => {
    const estate = await getEstate(estateId);
    openModal('copy-project-object', {
      navigate,
      estate: {
        ...estate,
        projectId: project?.id,
      },
      onSubmit: () => {
        queryClient.invalidateQueries({
          queryKey: getProjectEstatesOptions(queryProjectEstatesOptions).queryKey,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getProjectEstatesOptions(queryProjectEstatesOptions).queryKey,
        });
      },
    });
  };

  const onAddObjectButtonClick = useCallback(() => {
    openModal('create-project-object', {
      navigate,
      project: {
        id: project?.id || '',
        name: project?.name || '',
      },
    });
  }, [navigate, project]);

  const handleDeleteEstate = (estateId: string) => {
    openModal('confirm-delete-modal', {
      title: t('deleteEstate'),
      description: t('confirmDeleteEstate'),
      onSubmit: () => {
        deleteEstate(estateId);
      },
    });
  };

  const buttonAddObject = useMemo(
    () => (
      <Button
        size="xl"
        variant="outline"
        className="flex justify-center gap-[5px] px-[20px] lg:px-10 xl:px-3"
        onClick={onAddObjectButtonClick}
      >
        <PlusIcon />
        <span className="block max-xl:hidden">{t('add-new-estate')}</span>
      </Button>
    ),
    [onAddObjectButtonClick, t]
  );

  return (
    <div className="flex size-full flex-col gap-4">
      <Toolbar url={url} showViewSwitch={false} rightButtonAction={buttonAddObject} />
      <Card className="px-8 py-6 lg:px-20">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-md text-primary-dark">{project?.name}</h2>
            <h5 className="text-sm text-text-secondary">
              {totalEstates} {t('linked-objects-in-totals')}
            </h5>
          </div>
        </div>

        {estates.length ? (
          <Table className="my-5 w-full table-auto bg-transparent [&_thead_tr]:border-b-0">
            <TableHeader className="h-11 rounded-[10px] bg-[#EDEDED] max-sm:hidden">
              <TableRow className="w-full justify-between rounded-[10px]">
                <TableHead className="rounded-bl-[10px] rounded-tl-[10px] text-primary-dark">
                  {t('thumbnail')}
                </TableHead>
                <TableHead className="text-primary-dark">{t('external-id')}</TableHead>
                <TableHead className="text-primary-dark">{t('object-name')}</TableHead>
                <TableHead className="text-primary-dark">{t('floor')}</TableHead>
                <TableHead className="text-primary-dark">{t('rooms')}</TableHead>
                <TableHead className="text-primary-dark">{t('area')}</TableHead>
                <TableHead className="text-primary-dark">{t('building')}</TableHead>
                <TableHead className="text-primary-dark">{t('price')}</TableHead>
                <TableHead className="text-primary-dark">{t('status')}</TableHead>
                <TableHead className="rounded-br-[10px] rounded-tr-[10px] text-primary-dark" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {estates.map((estate) => (
                <TableRow
                  key={`project-object-${estate.id}`}
                  className="cursor-pointer items-center text-primary-dark"
                  onClick={() => {
                    navigateToEstatePage(estate.id);
                  }}
                >
                  <EstateTableRow
                    estate={estate}
                    handleCopyClick={handleCopyClick}
                    handleDeleteEstate={handleDeleteEstate}
                  />
                </TableRow>
              ))}
              <ProjectEstatesInfiniteScroll
                projectId={project?.id}
                filters={filters}
                navigateToEstatePage={navigateToEstatePage}
                rowRenderer={(estate) => (
                  <EstateTableRow
                    key={`project-object-${estate.id}`}
                    estate={estate}
                    handleCopyClick={handleCopyClick}
                    handleDeleteEstate={handleDeleteEstate}
                  />
                )}
              />
            </TableBody>
          </Table>
        ) : (
          <div className="flex justify-center py-16">
            <span>{t('no-estates')}</span>
          </div>
        )}
      </Card>
    </div>
  );
};

const EstateTableRow: React.FC<{
  estate: ProjectEstates[number];
  handleCopyClick: (estateId: string) => void;
  handleDeleteEstate: (estateId: string) => void;
}> = ({ estate, handleCopyClick, handleDeleteEstate }) => {
  const { t } = useTranslation();
  return (
    <>
      {/* Thumbnail */}
      <TableCell className="whitespace-normal break-words py-5 max-sm:hidden">
        <EstateImage
          className="h-16 w-28 rounded"
          thumbnailId={estate.thumbnail?.mediaId}
          iconSize={28}
        />
      </TableCell>
      {/* External ID */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 max-sm:hidden">
        {estate.estateDetails?.externalId}
      </TableCell>
      {/* Object name */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 max-sm:hidden">
        {estate.name}
      </TableCell>
      {/* Floor */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 uppercase max-sm:hidden">
        {typeof estate.estateAreasRooms?.floor === 'number'
          ? getFloorLabel(estate.estateAreasRooms?.floor)
          : ''}
      </TableCell>
      {/* Rooms */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 capitalize max-sm:hidden">
        {estate.estateAreasRooms?.totalNumberOfRooms
          ? `${getNumberFormatter(estate.estateAreasRooms?.totalNumberOfRooms, 'de-DE', {
              maximumFractionDigits: 1,
              minimumFractionDigits: 1,
            })} ${t('rooms')}`
          : ''}
      </TableCell>
      {/* Area */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 max-sm:hidden">
        {estate.estateAreasRooms?.livingArea
          ? `${getNumberFormatter(estate.estateAreasRooms?.livingArea, 'de-DE')}${t('m2')}`
          : ''}
      </TableCell>
      {/* Building */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 max-sm:hidden">
        {estate.estateDetails?.building}
      </TableCell>
      {/* Price */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 max-sm:hidden">
        {estate.prices?.buyingPrice
          ? getNumberFormatter(estate.prices?.buyingPrice, 'de-DE', {
              maximumFractionDigits: 1,
              minimumFractionDigits: 1,
            })
          : ''}
      </TableCell>
      {/* Status */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 capitalize max-sm:hidden">
        {estate.status}
      </TableCell>
      {/* Link */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 max-sm:hidden">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 w-7 border-primary-light text-primary-light"
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              handleDeleteEstate(estate.id);
            }}
          >
            <TrashIcon />
          </Button>
          <Button
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              handleCopyClick(estate.id);
            }}
            size="sm"
            variant="outline"
            className="h-7 w-7 border-primary-light text-primary-light"
          >
            <CopyIcon />
          </Button>
        </div>
      </TableCell>
    </>
  );
};
